import { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, Tooltip, Typography, alpha } from '@mui/material';
import 'mathlive';
import '../mathlive.d.ts';
import { latexToPlainText } from '../utils/latexConverter';
import './MathInput.css';

const KEPT_WORD_SHORTCUTS = new Set(['sqrt', 'cbrt', 'nthroot', 'infty', 'pi', 'theta']);

// MathLive's virtual keyboard calls preventDefault() on `pointerdown`, which
// keeps focus in the math field for mouse input. On touch devices, browsers
// still fire a compatibility `mousedown` after the tap, and that moves focus
// off the math field on every key press. Cancel those mousedowns once,
// globally, for anything inside the keyboard or its long-press variants panel.
let virtualKeyboardFocusGuardInstalled = false;
function installVirtualKeyboardFocusGuard() {
  if (virtualKeyboardFocusGuardInstalled || typeof document === 'undefined') return;
  virtualKeyboardFocusGuardInstalled = true;
  document.addEventListener(
    'mousedown',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.ML__keyboard, .MLK__variant-panel')) {
        event.preventDefault();
      }
    },
    true
  );
}

interface MathInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

export default function MathInput({ value, onChange, disabled = false, label, onKeyDown }: MathInputProps) {
  const mathFieldRef = useRef<any>(null);
  const [isFocused, setIsFocused] = useState(false);

  const focusMathField = (moveCaretToEnd = false) => {
    const field = mathFieldRef.current;
    if (!field) return;

    field.focus();
    if (moveCaretToEnd) {
      field.executeCommand?.('moveToMathfieldEnd');
    }
  };

  const handleContainerMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;

    // MathLive only handles pointer events on its inner formula area
    // ([part=content]). Clicks anywhere else (wrapper padding, or the empty
    // space inside <math-field> above/below the formula) fall through to the
    // browser, which places a native caret in MathLive's hidden contenteditable
    // keyboard sink. That produces a second caret and swallows keystrokes.
    // Intercept those clicks and hand focus to MathLive ourselves.
    const path = event.nativeEvent.composedPath();
    const hitFormula = path.some(
      (node) => node instanceof Element && node.getAttribute('part') === 'content'
    );
    if (hitFormula) return;

    event.preventDefault();
    focusMathField(true);
  };

  // Configure MathLive options once on mount
  useEffect(() => {
    installVirtualKeyboardFocusGuard();
    if (mathFieldRef.current) {
      // Set properties directly instead of using deprecated setOptions()
      mathFieldRef.current.smartFence = false;
      mathFieldRef.current.smartMode = false;
      mathFieldRef.current.removeExtraneousParentheses = false;
      mathFieldRef.current.mathVirtualKeyboardPolicy = 'manual';
      mathFieldRef.current.menuItems = [];

      // MathLive's default word shortcuts fire on substrings, so typing
      // "undefined" becomes "undef\in ed" and "does not exist" gets \neg and
      // \exists. Drop the alphabetic shortcuts, keeping symbol ones (<=, +-)
      // and a few unambiguous math words.
      const defaults: Record<string, unknown> = mathFieldRef.current.inlineShortcuts ?? {};
      mathFieldRef.current.inlineShortcuts = Object.fromEntries(
        Object.entries(defaults).filter(
          ([key]) => !/^[a-zA-Z]+$/.test(key) || KEPT_WORD_SHORTCUTS.has(key)
        )
      );
    }
  }, []);

  // Clear the field when value is empty (after submission)
  useEffect(() => {
    if (!value && mathFieldRef.current) {
      if (mathFieldRef.current.value !== '') {
        mathFieldRef.current.value = '';
        // Refocus after clearing to ensure it's ready for input
        setTimeout(() => {
          focusMathField();
        }, 0);
      }
    }
  }, [value]);

  const handleInput = (evt: React.ChangeEvent<HTMLElement>) => {
    const target = evt.target as any;
    const latex = target.value || '';
    const plainText = latexToPlainText(latex);
    onChange(plainText);
  };

  const insertLatex = (latex: string) => {
    if (mathFieldRef.current && !disabled) {
      focusMathField();
      mathFieldRef.current.insert(latex);
      // Trigger input event to update the value
      const plainText = latexToPlainText(mathFieldRef.current.value || '');
      onChange(plainText);
    }
  };

  return (
    <Box>
      {label && (
        <Box sx={{ mb: 1, fontSize: '1rem', fontWeight: 500, color: 'text.secondary' }}>
          {label}
        </Box>
      )}

      <Box
        onMouseDown={handleContainerMouseDown}
        sx={{
          width: '100%',
          minHeight: '60px',
          p: 1.5,
          border: '1px solid',
          borderColor: isFocused ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          cursor: disabled ? 'not-allowed' : 'text',
          display: 'flex',
          alignItems: 'center',
          boxShadow: isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
        }}
      >
        {/* @ts-ignore - MathLive web component */}
        <math-field
          ref={mathFieldRef}
          virtual-keyboard-mode="off"
          style={{
            display: 'block',
            width: '100%',
            minHeight: '32px',
            fontSize: '20px',
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
          onInput={handleInput}
          onKeyDown={onKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
        >
          {/* @ts-ignore */}
        </math-field>
      </Box>
      <Typography variant="caption" color="text.secondary" id="answer-hint" sx={{ mt: 1, display: 'block' }}>
            If needed, use the toolbox buttons for special symbols
      </Typography>

      {/* Math symbol buttons */}
      <Box className="math-input-toolbar" sx={{ mt: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {/* Grouping and structure */}
        <ButtonGroup
          className="math-input-button-group"
          size="small"
          variant="outlined"
          disabled={disabled}
          sx={(theme) => ({
            maxWidth: '100%',
            // On phones, let the buttons wrap onto extra rows instead of
            // overflowing off-screen. Each button becomes a standalone
            // outlined button with its own full border and rounded corners.
            [theme.breakpoints.down('sm')]: {
              flexWrap: 'wrap',
              gap: 1,
              '& .MuiButtonGroup-grouped.MuiButtonGroup-grouped': {
                borderRadius: `${theme.shape.borderRadius}px`,
                marginLeft: 0,
                borderRightColor: alpha(theme.palette.primary.main, 0.5),
              },
              '& .MuiButtonGroup-grouped.MuiButtonGroup-grouped.Mui-disabled': {
                borderRightColor: theme.palette.action.disabledBackground,
              },
            },
          })}
        >
        {/* Operations - Order of operations */}
          <Tooltip title="Exponent / Power">
            <Button onClick={() => insertLatex('#1^{#2}')}>xⁿ</Button>
          </Tooltip>
          <Tooltip title="Square root">
            <Button onClick={() => insertLatex('\\sqrt{#0}')}>√</Button>
          </Tooltip>
          <Tooltip title="Fraction / Division">
            <Button onClick={() => insertLatex('\\frac{#1}{#2}')}>÷</Button>
          </Tooltip>

        {/* Special values and sets */}
          <Tooltip title="Infinity">
            <Button onClick={() => insertLatex('\\infty')}>∞</Button>
          </Tooltip>
          <Tooltip title="Negative infinity">
            <Button onClick={() => insertLatex('-\\infty')}>-∞</Button>
          </Tooltip>
          <Tooltip title="Interval notation (open)">
            <Button onClick={() => insertLatex('(#1,#2)')}>( , )</Button>
          </Tooltip>
          <Tooltip title="Interval notation (closed)">
            <Button onClick={() => insertLatex('[#1,#2]')}>[ , ]</Button>
          </Tooltip>
          <Tooltip title="Union of sets">
            <Button onClick={() => insertLatex('\\cup')}>∪</Button>
          </Tooltip>
          <Tooltip title="Empty set / No solution">
            <Button onClick={() => insertLatex('\\emptyset')}>∅</Button>
          </Tooltip>
        </ButtonGroup>
      </Box>
    </Box>
  );
}
